import { useEffect, useState } from "react";
import { FullPageNotice } from "@/components/FullPageNotice";
import { SESSION_BOOTSTRAP_TIMEOUT_MS } from "@/components/auth/SessionBootstrap";

/**
 * What the onboarding gate's `"wait"` state renders once waiting stops being a
 * wait and becomes a hang.
 *
 * BEN-141 #1201. `"wait"` returning `null` is the right answer for the ordinary
 * case and stays that way: a flash of chrome ahead of a redirect into the wizard
 * reads worse than a blank frame, and guessing "new user" from an unresolved
 * profile is what sent established accounts back into setup (QF4). But the wait
 * had **no ceiling**. `onboardingGateDecision` returns `"wait"` for
 * `status === "pending"`, and a request that never answers stays pending, so the
 * bound on the blank page was the bound on the request — minutes, for a dropped
 * connection or a stalled proxy. Measured on the deployed dev host with
 * `/api/profile/me/` hanging: `/dashboard` held 0 characters for as long as the
 * request did, while `/payment` rendered 1,289.
 *
 * The deliberate trade was to wait rather than guess. That trade is still right;
 * it just needed an end.
 *
 * It reuses `SESSION_BOOTSTRAP_TIMEOUT_MS` rather than declaring its own five
 * seconds, because the two screens answer the same question a few hundred
 * milliseconds apart and a user who meets both should not meet two timings.
 *
 * The action refetches the profile rather than reloading the page: the session
 * is already established here, so there is nothing to rebuild, and a reload
 * would cost the user the route they were on.
 *
 * It lives in its own file because `onboardingGateSurface.tsx` exports a
 * factory rather than a component, and mixing the two trips
 * `react-refresh/only-export-components`.
 */
export function GateWaitNotice({ onRetry }: { onRetry: () => void }) {
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setStalled(true), SESSION_BOOTSTRAP_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, []);

  if (!stalled) return null;

  return (
    <FullPageNotice
      title="We could not load your account"
      actionLabel="Try again"
      onAction={onRetry}
    >
      Your account details did not arrive, so this page could not open. This is usually a slow or
      interrupted connection rather than a problem with your account. Try again, and if it keeps
      happening, reload the page.
    </FullPageNotice>
  );
}
